import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtDate } from "../lib/utils";
import { useI18n } from "../lib/i18n";
import {
  UserCircle, CheckCircle, XCircle, Storefront, Buildings, Envelope, Phone,
  Sparkle, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

export default function AdminPendingActivations() {
  const { lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pending-activations");
      setRows(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (userId) => {
    if (!window.confirm("Confermi l'attivazione di questo cliente? Riceverà l'email di benvenuto.")) return;
    setBusyId(userId);
    try {
      await api.post(`/admin/pending-activations/${userId}/approve`);
      toast.success("Cliente attivato ✓");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore attivazione"); }
    finally { setBusyId(null); }
  };

  const reject = async (userId) => {
    if (!window.confirm("Sei sicuro di voler rifiutare questo cliente? Non potrà più accedere.")) return;
    setBusyId(userId);
    try {
      await api.post(`/admin/pending-activations/${userId}/reject`);
      toast.success("Cliente rifiutato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-pending-activations">
      <PageHeader
        eyebrow="Onboarding"
        title="Attivazioni in attesa"
        subtitle="Verifica i nuovi clienti registrati tramite il wizard pubblico e attiva la loro dashboard."
        chip="tf-chip-lime"
        icon={UserCircle}
      />

      {loading ? (
        <div className="tf-card p-10 text-center text-[var(--tf-muted)]">Caricamento…</div>
      ) : rows.length === 0 ? (
        <div className="tf-card p-10 text-center">
          <div className="tf-chip tf-chip-mint mx-auto mb-4"><CheckCircle size={22} weight="fill" /></div>
          <div className="font-display font-bold text-lg">Nessuna attivazione in attesa</div>
          <p className="text-sm text-[var(--tf-muted)] mt-1">Ottimo lavoro. Tutti i nuovi iscritti sono già stati processati.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {rows.map(({ user, client }) => (
            <div key={user.id} className="tf-card p-6" data-testid={`pending-${user.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                  {client?.logo_url ? (
                    <img src={client.logo_url} alt="logo" className="w-14 h-14 rounded-2xl object-contain border border-[var(--tf-border)] bg-white" />
                  ) : (
                    <div className="tf-chip tf-chip-lavender" style={{ width: 56, height: 56, borderRadius: 18 }}>
                      <Storefront size={22} weight="fill" />
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] flex items-center gap-1.5">
                      <Sparkle size={10} weight="fill" /> Iscritto {user.created_at ? fmtDate(user.created_at, lang) : "—"}
                    </div>
                    <div className="font-display font-black text-xl leading-tight mt-1">{client?.company_name || user.name}</div>
                    <div className="text-xs text-[var(--tf-muted)] mt-0.5">P.IVA {client?.vat || "—"}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="tf-status-pill tf-status-warning">In attesa</div>
                  {client?.subscription_plan && (
                    <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] mt-2">
                      Piano <b className="text-[var(--tf-ink)]">{client.subscription_plan}</b> · {client.subscription_period}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                <InfoRow icon={UserCircle} label="Contatto" value={user.name} />
                <InfoRow icon={Envelope} label="Email" value={user.email} />
                {client?.phone && <InfoRow icon={Phone} label="Telefono" value={client.phone} />}
                {client?.store_url && (
                  <InfoRow
                    icon={Storefront}
                    label={`Store · ${client.store_platform || ""}`}
                    value={<a href={client.store_url} target="_blank" rel="noopener noreferrer" className="text-[var(--tf-ink)] underline inline-flex items-center gap-1">{client.store_name || client.store_url} <ArrowSquareOut size={10} weight="bold" /></a>}
                  />
                )}
                {client?.address_line1 && <InfoRow icon={Buildings} label="Sede" value={`${client.address_line1}, ${client.city} ${client.postal_code}`} />}
                {client?.sdi_or_pec && <InfoRow icon={Envelope} label="SDI/PEC" value={client.sdi_or_pec} />}
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-[var(--tf-border)]">
                <button
                  onClick={() => reject(user.id)}
                  disabled={busyId === user.id}
                  className="tf-btn tf-btn-ghost !text-[var(--tf-error)]"
                  data-testid={`reject-${user.id}`}
                >
                  <XCircle size={16} weight="bold" /> Rifiuta
                </button>
                <button
                  onClick={() => approve(user.id)}
                  disabled={busyId === user.id}
                  className="tf-btn tf-btn-primary flex-1"
                  data-testid={`approve-${user.id}`}
                >
                  <CheckCircle size={16} weight="fill" /> {busyId === user.id ? "Attivazione…" : "Attiva dashboard"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] flex items-center gap-1.5 mb-1">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className="text-xs text-[var(--tf-ink)] truncate">{value}</div>
    </div>
  );
}
